import cx from 'classnames'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
  useId,
  useState,
} from 'react'
import { Text } from '../Text'
import styles from './Accordion.module.css'

export type AccordionHeadingLevel = 2 | 3 | 4 | 5 | 6

export interface AccordionProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /**
   * The main line of the header.
   *
   * A string is shown as `Text` with the `section-title` variant.
   * Pass a `Text` component to choose the text style yourself.
   */
  title: ReactNode
  /**
   * A second, quieter line shown under the title.
   *
   * A string is shown as `Text` with the `supporting` variant.
   * Pass a `Text` component to choose the text style yourself.
   */
  supporting?: ReactNode
  /**
   * Shown at the end of the header, before the chevron,
   * eg: an amount or a status chip.
   *
   * It sits inside the header button, so it must not hold
   * anything that can be clicked or focused.
   */
  trailing?: ReactNode
  /**
   * Buttons that act on the panel content, eg: "Select All".
   * Shown at the top of the panel, aligned to the end.
   */
  actions?: ReactNode
  /**
   * Whether the panel is open. Pass this with `onOpenChange`
   * to control the accordion from the caller, eg: to keep only
   * one row of a group open at a time.
   */
  open?: boolean
  /** Whether the panel starts open, when `open` is not passed */
  defaultOpen?: boolean
  /** Called with the next open state when the header is pressed */
  onOpenChange?: (open: boolean) => void
  /** Stops the header from being pressed */
  disabled?: boolean
  /**
   * Wraps the header button in a heading of this level,
   * so the title shows up in the page outline.
   */
  headingLevel?: AccordionHeadingLevel
}

/**
 * A header button with a chevron that opens a panel.
 *
 * On its own it renders as a card. Place several inside an
 * `AccordionGroup` to render them as rows of one card.
 */
export function Accordion({
  title,
  supporting,
  trailing,
  actions,
  open,
  defaultOpen = false,
  onOpenChange,
  disabled = false,
  headingLevel,
  className,
  children,
  ...props
}: PropsWithChildren<AccordionProps>) {
  const panelId = `${useId()}-panel`

  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const isOpen = open ?? internalOpen

  const ChevronIcon = isOpen ? ExpandLessIcon : ExpandMoreIcon

  const header = (
    <button
      type="button"
      className={styles.Accordion__header}
      aria-expanded={isOpen}
      aria-controls={panelId}
      disabled={disabled}
      onClick={() => {
        if (open === undefined) setInternalOpen(!isOpen)
        onOpenChange?.(!isOpen)
      }}
    >
      <span className={styles.Accordion__headerText}>
        {typeof title === 'string' ? (
          <Text variant="section-title" component="span">
            {title}
          </Text>
        ) : (
          title
        )}
        {typeof supporting === 'string' ? (
          <Text variant="supporting" component="span">
            {supporting}
          </Text>
        ) : (
          supporting
        )}
      </span>
      {trailing && (
        <span className={styles.Accordion__trailing}>
          {trailing}
        </span>
      )}
      <ChevronIcon
        className={styles.Accordion__chevron}
        aria-hidden="true"
      />
    </button>
  )

  const Heading = headingLevel
    ? (`h${headingLevel}` as const)
    : undefined

  return (
    <div className={cx(styles.Accordion, className)} {...props}>
      {Heading ? (
        <Heading className={styles.Accordion__heading}>
          {header}
        </Heading>
      ) : (
        header
      )}
      <div
        id={panelId}
        className={styles.Accordion__panel}
        hidden={!isOpen}
      >
        {actions && (
          <div className={styles.Accordion__actions}>
            {actions}
          </div>
        )}
        {children}
      </div>
    </div>
  )
}

export interface AccordionGroupProps
  extends HTMLAttributes<HTMLDivElement> {}

/**
 * Renders the accordions inside it as rows of one card,
 * with a hairline between each row.
 */
export function AccordionGroup({
  className,
  children,
  ...props
}: PropsWithChildren<AccordionGroupProps>) {
  return (
    <div
      className={cx(styles.AccordionGroup, className)}
      {...props}
    >
      {children}
    </div>
  )
}
